<template>
  <div class="bg-gray-50 border-b last:border-b-0 dark:bg-gray-900">
    <o-collapse
      header-class="flex flex-col md:flex-row p-1 md:p-3 items-center justify-between"
      icon="edit"
      :clickable="false"
    >
      <template #toggle="{ toggle }">
        <div class="w-full md:w-auto justify-between flex space-x-1 items-center p-2">
          <o-button
            size="sm"
            flat
            variant="danger"
            icon="delete"
            :disabled="!allowRemove"
            @click.prevent.stop="handleRemove()"
          />

          <o-button
            size="sm"
            flat
            icon="accordion"
            @click="toggle()"
          />
        </div>
      </template>

      <template #header>
        <div class="w-full flex flex-wrap md:flex-nowrap space-y-3 md:space-y-0 space-x-3">
          <div class="hidden md:block self-stretch relative pr-1">
            <div
              class="w-1 rounded h-full"
              :style="{
                backgroundColor: colour
              }"
            />
          </div>
          <o-field-group :label="$t('general.service')" class="w-full md:w-1/3" label-class="text-xs uppercase mb-1 text-gray-600 dark:text-gray-300">
            <div v-if="!!_service.association" class="flex items-center">
              <o-avatar :items="[ _service.service ]" class="hidden md:flex pr-2" />

              <p class="text-sm text-gray-800 leading-none dark:text-gray-100 pr-3">
                {{ _service.service.title }}
              </p>

              <o-chip size="xs" variant="primary" flat>
                {{ $t('service.addOn') }}
              </o-chip>
            </div>
            <service-select
              v-else
              v-model="_service.service"
              :type="serviceType"
              class="w-full bg-white dark:bg-gray-900"
              :ui="{
                content: 'max-h-[min(30rem,var(--reka-combobox-content-available-height,30rem))]',
              }"
              :disabled="!!_service.association"
              @change="serviceChanged"
            />
          </o-field-group>

          <o-field-group :label="$t('general.employee')" class="w-full md:w-1/3" label-class="text-xs uppercase mb-1 text-gray-600 dark:text-gray-300">
            <employee-select
              v-model="_service.employee"
              :types="['employee']"
              class="w-full bg-white dark:bg-gray-900"
              :ui="{ base: 'min-h-10' }"
              :filter="employeeFilter"
              :disabled="!hasService"
              allow-any
              @change="employeeChanged"
            />
          </o-field-group>

          <div class="w-full md:w-1/3 flex items-start">
            <o-field-group class="w-1/2" :label="$t('general.duration')" label-class="text-xs uppercase mb-1 text-gray-600 dark:text-gray-300">
              <div class="my-2 flex h-full items-center space-x-1">
                <p class="text-sm text-gray-800 leading-none dark:text-gray-100">
                  {{ durationLabel }}
                </p>
                <o-tooltip v-if="isDurationModified" :text="$t('service.durationModified')">
                  <o-icon icon="warningBold" fill class="text-yellow-600 mx-1 dark:text-yellow-400" />
                </o-tooltip>
              </div>
            </o-field-group>
            <o-field-group class="w-1/2" :label="$t('general.price')" label-class="text-xs uppercase mb-1 text-gray-600 dark:text-gray-300">
              <div class="my-2 flex h-full items-center space-x-1">
                <p class="text-sm text-gray-800 leading-none dark:text-gray-100">
                  <template v-if="hasAddOnDiscount">
                    <span class="opacity-70 line-through pr-1">{{ priceLabel }}</span>
                    <span v-if="_service.association.target.priceFrom" class="pr-1">{{ $t('general.from') }}</span>{{ discountedPrice }}
                  </template>
                  <span v-else>
                    {{ priceLabel }}
                  </span>
                </p>
                <o-tooltip v-if="isPriceModified" :text="$t('service.priceModified')">
                  <o-icon icon="warningBold" fill class="text-yellow-600 mx-1 dark:text-yellow-400" />
                </o-tooltip>
              </div>
            </o-field-group>
          </div>
        </div>
      </template>

      <o-form ref="form">
        <div class="px-3 py-4 border-t bg-gray-100 dark:bg-gray-800">
          <div class="w-full flex flex-wrap md:flex-nowrap space-y-3 md:space-y-0 md:space-x-3">
            <o-field-group
              :label="$t('general.price')"
              :name="$t('general.price')"
              rules="required"
              :modified="isPriceModified"
              :modified-message="$t('service.priceModified')"
              class="w-full md:w-1/3"
            >
              <template #default="{ status }">
                <currency-field v-model="_service.price" :disabled="!hasService" :status="status" />
              </template>
            </o-field-group>

            <o-field-group
              :label="$t('components.reservation.create.service.timings')"
              :modified="isTimingModified"
              :modified-message="$t('components.reservation.create.service.timingInfo')"
              class="w-full md:w-2/3"
            >
              <o-dropdown class="w-full">
                <o-dropdown-field :disabled="!hasService" class="w-full bg-white shadow-sm rounded-md dark:bg-gray-900">
                  <p class="text-sm text-gray-800 dark:text-gray-100">
                    {{ $t('components.reservation.create.service.durationProcessingFinishing', { duration: _service.duration, processing: _service.processing, finishing: _service.finishing }) }}
                  </p>
                </o-dropdown-field>

                <template #content>
                  <div class="p-2 max-w-xs">
                    <div class="flex items-center flex-wrap">
                      <o-field-group :label="$t('general.duration')" :modified="isDurationModified" :modified-message="$t('service.durationModified')" class="w-1/3 px-1 py-1">
                        <number-field v-model="_service.duration" slim :min="0" />
                      </o-field-group>

                      <o-field-group label="Processing" :modified="isProcessingModified" :modified-message="$t('service.processingModified')" class="w-1/3 px-1 py-1">
                        <number-field v-model="_service.processing" slim />
                      </o-field-group>

                      <o-field-group label="Finishing" :modified="isFinishingModified" :modified-message="$t('service.finishingModified')" class="w-1/3 px-1 py-1">
                        <number-field v-model="_service.finishing" slim />
                      </o-field-group>

                      <o-field-group v-if="hasPreBuffer" :modified="isPreBufferModified" :modified-message="$t('service.preBufferModified')" :label="$t('service.preBuffer')" class="w-1/3 px-1 py-1">
                        <number-field v-model="_service.bufferTimePre" slim />
                      </o-field-group>

                      <o-field-group v-if="hasPostBuffer" :modified="isPostBufferModified" :modified-message="$t('service.postBufferModified')" :label="$t('service.postBuffer')" class="w-1/3 px-1 py-1">
                        <number-field v-model="_service.bufferTimePost" slim />
                      </o-field-group>
                    </div>
                  </div>
                </template>
              </o-dropdown>
            </o-field-group>
          </div>

          <template v-if="hasResources">
            <div class="w-full flex flex-wrap mt-4 -mx-2">
              <template v-for="(resource, resourceIndex) in service.resources" :key="resourceIndex">
                <!-- New reservation -->
                <template v-if="'resources' in resource">
                  <o-field-group
                    v-for="(resourceSlot, resourceSlotIndex) in resource.resources"
                    :key="`${resourceIndex}-${resourceSlotIndex}`"
                    :label="resource.serviceResource.name + (resource.resources.length > 1 ? ` - ${resourceSlotIndex + 1}` : '')"
                    class="w-full md:w-1/2 px-2 mb-3"
                  >
                    <employee-select
                      v-model="resource.resources[resourceSlotIndex]"
                      class="w-full bg-white shadow-sm rounded-md dark:bg-gray-900"
                      :filter="($event) => resourceFilter($event, resource.serviceResource)"
                      :disabled="!hasService"
                      allow-any
                      allow-none
                    />
                  </o-field-group>
                </template>

                <!-- Existing reservation -->
                <template v-if="'resource' in resource">
                  <o-field-group
                    :key="resource.idReservationResource"
                    :label="resource.serviceResource.name"
                    class="w-full md:w-1/2 px-2 mb-3"
                  >
                    <employee-select
                      v-model="_service.resources[resourceIndex].resource"
                      class="w-full bg-white shadow-sm rounded-md dark:bg-gray-900"
                      :filter="($event) => resourceFilter($event, resource.serviceResource)"
                      :disabled="!hasService"
                      allow-any
                      allow-none
                    />
                  </o-field-group>
                </template>
              </template>
            </div>
          </template>

          <!--<template v-if="isCustomerTypeGroup">
            <div class="w-full mt-2 pt-3 border-t">
              <o-field-group :label="$t('reservation.allowOnline')" :info="$t('reservation.allowOnlineInfo')" class="pb-4 block">
                <toggle-field v-model="_service.allowInternet" />
              </o-field-group>
              <o-field-group :label="$t('general.maxCustomers')" :info="$t('reservation.maxCustomersInfo')" class="">
                <number-field v-model="_service.customerMax" />
              </o-field-group>
            </div>
          </template>-->
        </div>
      </o-form>
    </o-collapse>
  </div>
</template>

<script setup lang="ts">
import EmployeeSelect from '@/components/employee/elements/Select.vue'
import ServiceSelect from '@/components/service/elements/Select.vue'

import {
  ODropdownField,
  OAvatar,
} from '@/components/elements'

import {
  TextField,
  CurrencyField,
  NumberField,
  TimeField,
  ToggleField,
} from '@/components/fields'

import { ServiceAssociation } from '@/_api/models/serviceAssociation'
import { ServiceResourceAssignment } from '@/_api/models/serviceResource'

const { $auth } = useNuxtApp()

interface Props {
  staging: Record<string, any>
  service: Record<string, any>
}

interface Emits {
  (e: 'update:service', value: any): void
  (e: 'update:staging', value: any): void
  (e: 'remove'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const form = ref<any>()

const _service = computed({
  get () {
    return props.service
  },
  set (value) {
    emit('update:service', value)
  },
})

const _staging = computed({
  get () {
    return props.staging
  },
  set (value) {
    emit('update:staging', value)
  },
})

const serviceType = computed(() => {
  const services = props.staging.services
  if (isCustomerTypeGroup.value || (hasGroupService.value && services.length > 1)) {
    return 'group'
  }

  if (hasSingleService.value && services.length > 1) {
    return 'single'
  }

  return null
})

const allowRemove = computed(() => true)

const colour = computed(() => _service.value.colour)

const priceLabel = computed(() => {
  if (hasService.value) {
    return $auth.info.location.currencySymbol + props.service.price
  }

  return '-'
})

const startLabel = computed(() => _service.value.startLabel)
const durationLabel = computed(() => _service.value.durationLabel)
const hasService = computed(() => _service.value.hasService)
const hasResources = computed(() => _service.value.resources?.length)
const hasPreBuffer = computed(() => _service.value.hasPreBuffer)
const hasPostBuffer = computed(() => _service.value.hasPostBuffer)
const hasStartTime = computed(() => _service.value.hasStartTime)
const hasSingleService = computed(() => props.staging.hasSingleService)
const hasGroupService = computed(() => props.staging.hasGroupService)
const isPriceModified = computed(() => _service.value.isPriceModified)
const isTimingModified = computed(() => _service.value.isTimingModified)
const isDurationModified = computed(() => _service.value.isDurationModified)
const isProcessingModified = computed(() => _service.value.isProcessingModified)
const isFinishingModified = computed(() => _service.value.isFinishingModified)
const isPreBufferModified = computed(() => _service.value.isPreBufferModified)
const isPostBufferModified = computed(() => _service.value.isPostBufferModified)
const isCustomerTypeGroup = computed(() => props.staging.isCustomerTypeGroup)
const hasAddOnDiscount = computed(() => Number(_service.value.association?.discountAmount) > 0)
const discountedPrice = computed(() => ServiceAssociation.formatDiscountedPrice(
  _service.value.association,
  $auth.info.location.currencySymbol,
))

function serviceChanged () {
  props.service.setValues()

  const assignment = props.staging.isCustomerTypeGroup || props.service?.isServiceCustomerTypeSingle ? ServiceResourceAssignment.reservation : ServiceResourceAssignment.customer
  props.service.setResources(assignment)

  _staging.value.allowInternet = props.service.allowInternet
  _staging.value.customerMax = props.service.customerMax
}

function employeeChanged () {
  props.service.setValues()

  const assignment = props.staging.isCustomerTypeGroup || props.service?.isServiceCustomerTypeSingle ? ServiceResourceAssignment.reservation : ServiceResourceAssignment.customer
  props.service.setResources(assignment)

  _staging.value.allowInternet = props.service.allowInternet
  _staging.value.customerMax = props.service.customerMax
}

function employeeFilter (employee: any) {
  if (!employee.enabled) {
    return false
  }

  const service = props.service.service
  return service && service.employeeIDs ? service.employeeIDs.includes(employee.idEmployee) : false
}

function resourceFilter (resource: any, serviceResource: any) {
  return resource && serviceResource ? serviceResource.resourceIDs.includes(resource.idEmployee) : false
}

function handleRemove () {
  emit('remove')
}
</script>
